import { useEffect } from 'react';
import { FormattedMessage } from 'react-intl';
import { type Schedule } from '@accounts';
import { trackEvent } from '@/shared/analytics';
import { useRecheckSchedules } from '../hooks/useRecheckSchedules';
import { US_VISA_PORTAL_URL } from '../../stage/stages';

interface Props {
  accountId: number;
  onFound: (schedules: Schedule[]) => void;
}

export default function NoSchedules({ accountId, onFound }: Props) {
  const { recheck, isFetching } = useRecheckSchedules(accountId);

  useEffect(() => {
    trackEvent('no_schedules_shown', { account_id: accountId });
  }, [accountId]);

  const handleRecheck = async () => {
    trackEvent('no_schedules_rechecked', { account_id: accountId });
    onFound(await recheck());
  };

  return (
    <div>
      <h3 className="text-base font-semibold text-slate-900">
        <FormattedMessage id="preparation.noSchedules.title" />
      </h3>
      <p className="mt-3 text-sm text-slate-600">
        <FormattedMessage id="preparation.noSchedules.body" />
      </p>

      <div className="mt-6 flex flex-wrap items-center gap-4">
        <a
          href={US_VISA_PORTAL_URL}
          target="_blank"
          rel="noopener noreferrer"
          className="inline-flex items-center rounded-xl border border-slate-200 bg-white/80 px-5 py-3 text-sm font-medium text-slate-600 transition hover:border-slate-300 hover:text-slate-800 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-300"
        >
          <FormattedMessage id="preparation.noSchedules.portalLink" />
        </a>
        <button
          type="button"
          onClick={handleRecheck}
          disabled={isFetching}
          className="inline-flex items-center rounded-xl bg-brand-600 px-5 py-3 text-sm font-medium text-white transition hover:bg-brand-500 disabled:opacity-60 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-300"
        >
          <FormattedMessage
            id={isFetching ? 'preparation.noSchedules.rechecking' : 'preparation.noSchedules.recheck'}
          />
        </button>
      </div>
    </div>
  );
}
